import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_NAME, SITE_URL } from "@/components/base/Seo";
import SectionHeading from "@/components/base/SectionHeading";
import ContactForm from "./components/ContactForm";

const contactCards = [
  {
    icon: "ri-map-pin-2-line",
    label: "Visit our office",
    value: "2nd Floor, Balmatta Road, Mangaluru, Dakshina Kannada — 575001",
    href: "https://www.google.com/maps/search/?api=1&query=Balmatta+Road+Mangaluru+Karnataka",
  },
  {
    icon: "ri-phone-line",
    label: "Call us",
    value: "+91 98765 43210",
    href: "tel:+919876543210",
  },
  {
    icon: "ri-mail-line",
    label: "Email us",
    value: "info@coastalbizzconnect.com",
    href: "mailto:info@coastalbizzconnect.com",
  },
  {
    icon: "ri-time-line",
    label: "Office hours",
    value: "Monday to Saturday, 9:30 AM – 6:30 PM",
    href: "",
  },
];

export default function ContactPage() {
  return (
    <SiteLayout>
      <Seo
        title="Contact Us"
        description="Get in touch with Coastal Bizz Connect. Reach our Mangaluru team for listing support, advertising enquiries, partnerships or feedback about the coastal Karnataka business directory."
        keywords="contact Coastal Bizz Connect, business directory support Mangaluru, advertise business Karnataka, coastal Karnataka directory contact"
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Coastal Bizz Connect",
          url: `${SITE_URL}/contact`,
          isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
        }}
      />

      <section className="border-b border-background-200 bg-primary-900">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-12 md:py-16">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300" aria-label="Breadcrumb">
            <Link to="/" className="transition-colors hover:text-accent-300">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-background-100">Contact</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-4xl">
            We are here to help
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            Questions about listing your business, advertising or anything else? Reach out and our
            Mangaluru team will get back to you quickly.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_2fr]">
          <div className="flex flex-col gap-4">
            {contactCards.map((card) => {
              const inner = (
                <>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className={`${card.icon} text-xl`} />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
                      {card.label}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-800">{card.value}</p>
                  </div>
                </>
              );

              return card.href ? (
                <a
                  key={card.label}
                  href={card.href}
                  target={card.href.startsWith("http") ? "_blank" : undefined}
                  rel={card.href.startsWith("http") ? "nofollow noopener noreferrer" : undefined}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={card.label}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  {inner}
                </div>
              );
            })}

            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <iframe
                title="Coastal Bizz Connect office location in Mangaluru"
                src="https://www.google.com/maps?q=Balmatta%20Road%20Mangaluru%20Karnataka&output=embed"
                className="h-64 w-full border-0"
                aria-label="Map showing the Coastal Bizz Connect office in Mangaluru"
              />
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <SectionHeading
              eyebrow="Send a message"
              title="Tell us what you need"
              description="Fill in the form and our team will reply within one working day."
            />
            <div className="mt-6">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}